import { ERROR_MESSAGE_SERVICE, ErrorMessageService } from "../services/errorMessage.service";
import { removeFirstFromArray, wrapAsArray } from "../tools/collection.array.tools";
import { addToArrayRecord } from "../tools/collection.map.tools";
import { anyReallyAny } from "../tools/object.tools";
import { INJECTOR_DEPENDENCY_INJECTION_NAMES_VAR, INJECTOR_GET_ERROR_MESSAGES_FUNCTION_NAME_VAR } from "./injectable";


interface InjectorContext {
    /**
     * dependencies register map
     */
    registeredDependencies: { [dependencyName: string]: anyReallyAny };

    /**
     * This array contains all services that were registered before ErrorMessageService was registered.
     * When the ErrorMessageService will be registered, their messages will be registered on it
     */
    unregisteredErrorMessages: { service: anyReallyAny, getErrorMessagesFunctionName: string }[];
}

/**
 * Callback that can be registered as a listener on Injector
 * If listener returns { removeMe: true } it is then removed from listeners list
 */
type RegisterDependencyListener<Dependency = unknown> = (dependency: Dependency) => void | { removeMe: true };

/**
 * Class that stores the dependencies used by the dependency injection mechanism.
 * 
 * You can open a test context, all dependencies registered while test context is open
 * will be store separately and override globally registered dependencies.
 * When closing test context, all the dependencies registered within the test context are dismissed.
 */
class Injector {

    // TODO remove unregisteredErrorMessages from Injector and use addRegisterDependencyListener instead

    // Injector main context (there is also an optional test context)
    protected _context: InjectorContext = { registeredDependencies: {}, unregisteredErrorMessages: [] };

    // Dependency injection listeners
    protected _registerDependencyListeners: Record<string, RegisterDependencyListener[]> = {};

    /**
     * @returns the current context (the main one or a test context if one was open)
     */
    protected _getCurrentContext(): InjectorContext {
        return this._testContext ?? this._context;
    }

    /**
     * execute the callback if dependencyName is registered, it dependencyName is not registered yet, calls the callback the first time the dependency is registered
     * ! if dependencyName is registered several times, only the first register will execute callback
     */
    public executeWhenRegistered<Dep>(dependencyName: string, callback: (depencency: Dep) => void): void {
        const dependency = this.find(dependencyName);
        if (dependency) {
            // depencency is registered => execute now
            callback(dependency as Dep);
        } else {
            const listener: RegisterDependencyListener<Dep> = (dep: Dep) => {
                // execute callback
                callback(dep);
                // only once
                return { removeMe: true };
            };
            this.addRegisterDependencyListener(dependencyName, listener);
        }
    }

    /**
     * Adds a listener that should be notified every time a dependency is registered with dependencyName
     */
    public addRegisterDependencyListener<Dependency = unknown>(dependencyName: string, listener: RegisterDependencyListener<Dependency>): void {
        addToArrayRecord(this._registerDependencyListeners, dependencyName, listener);
    }

    /**
     * registers a dependency
     * @param dependencyName the name that will be used to retrieve the dependency
     * @param injected the service or object to register
     * @param options
     * @param options.ignoreIfDependencyRegistered if different from true an error will be thrown if dependencyName is already registered
     * @param options.overrideIfDependencyRegistered override with no error if dependencyName is already registered
     */
    public registerDependency(dependencyName: string, injected: anyReallyAny, options?: { ignoreIfDependencyRegistered?: boolean, overrideIfDependencyRegistered?: boolean }): void {
        // get current context register map (test or standard)
        const register = this._getCurrentContext().registeredDependencies;

        // set in register
        if (register[dependencyName]) {
            if (options?.overrideIfDependencyRegistered) {
                // continue and override
            } else if (options?.ignoreIfDependencyRegistered) {
                return;// ignore
            } else {
                throw new Error("dependency " + dependencyName + " was already registered");
            }
        }
        register[dependencyName] = injected;

        this._fireRegistration(dependencyName, injected);
    }

    /**
     * Call listeners and onServiceRegister for injected service
     */
    protected _fireRegistration(dependencyName: string, injected: anyReallyAny): void {
        // notify listeners
        const listeners = this._registerDependencyListeners[dependencyName];
        if (listeners) {
            // clone array to allow listeners list update without affecting current for loop
            const listenersClone = [...listeners];
            for (const listener of listenersClone) {
                try {
                    const ret = listener(injected);
                    if (ret?.removeMe) {
                        removeFirstFromArray(listeners, listener);
                    }
                } catch (e) {
                    // listeners must not have an impact on registration
                    console.error(e);
                }
            }
        }

        // call onServiceRegister callback
        try {
            if (injected.Injector_onServiceRegister) {
                injected[injected.Injector_onServiceRegister]();
            }
        } catch (e) {
            // listeners must not have an impact on registration
            console.error(e);
        }
    }

    /**
     * @returns the instance dependencyInjectionNames defined by the @Injectable decorator
     */
    public getInstanceDependencyInjectionNames(instance: anyReallyAny): string | string[] {
        const dependencyInjectionNames: string | string[] = instance[INJECTOR_DEPENDENCY_INJECTION_NAMES_VAR];
        if (!dependencyInjectionNames) {
            throw new Error("instance has no dependencyInjectionNames, did you decorate the class with @Injectable ?");
        }
        return dependencyInjectionNames;
    }

    /**
     * registers an @Injectable dependency
     * @param instance the service or object to register, the class needs to be decorated with @Injectable
     * @param options
     * @param options.ignoreIfDependencyRegistered if different from true an error will be thrown if dependencyName is already registered
     * @param options.overrideIfDependencyRegistered override with no error if dependencyName is already registered
     */
    public register(instance: anyReallyAny, options?: { ignoreIfDependencyRegistered?: boolean, overrideIfDependencyRegistered?: boolean }): void {
        const dependencyInjectionNames: string | string[] = this.getInstanceDependencyInjectionNames(instance);

        // instance provides several services
        for (const name of wrapAsArray(dependencyInjectionNames)) {
            this.registerDependency(name, instance, options);
        }

        this._registerInErrorMessagesServiceIfNeeded(instance, dependencyInjectionNames);
    }

    /**
     * unregisters the dependency
     */
    public unregister(dependencyName: string): void {
        // get current context register map (test or standard)
        const register = this._getCurrentContext().registeredDependencies;

        // delete from register
        delete register[dependencyName];
    }

    /**
     * unregisters the previous dependency and registers the @Injectable dependency
     */
    public override(instance: anyReallyAny): void {
        this.register(instance, { overrideIfDependencyRegistered: true });
    }

    /**
     * get the dependency and throws an error if dependency was not found
     */
    public get<T>(dependencyName: string): T {
        const dep = this.find<T>(dependencyName);
        if (!dep) { throw new Error("Dependency " + dependencyName + " was not found"); }
        return dep;
    }

    /**
     * get the dependency and returns undefined if dependency was not found
     */
    public find<T>(dependencyName: string): T | undefined {
        if (this._testContext?.registeredDependencies) {
            // if a test dependency is registered (even if set to null), it overrides the registered dependency
            const testDependency = this._testContext.registeredDependencies[dependencyName];
            if (testDependency !== undefined) {
                return testDependency;
            }
        }
        return this._context.registeredDependencies[dependencyName];
    }

    /**
     * @returns true if name is within dependencyInjectionNames
     */
    protected _isDependency(name: string, dependencyInjectionNames: string | string[]): boolean {
        return wrapAsArray(dependencyInjectionNames).includes(name);
    }

    //#region Test context

    // test context dependencies register map
    protected _testContext: InjectorContext | undefined = undefined;
    /**
     * Opens a test context.
     * If a test context was already open, the previous text context is closed and all its dependencies are dismissed.
     * 
     * All dependencies registered while test context is open will be store separately and override globally registered dependencies.
     * When closing test context, all the dependencies registered within the test context are dismissed.
     */
    public openNewTestContext(): void {
        this._testContext = { registeredDependencies: {}, unregisteredErrorMessages: [] };
    }

    /**
     * Closes the current test context.
     * When closing test context, all the dependencies registered within the test context are dismissed.
     */
    public closeTestContext(): void {
        const testContext = this._testContext;
        if (testContext) {
            // fire registration on services that were overridden
            for (const dependencyName in testContext) {
                const currentService = this.find(dependencyName);
                if (currentService != null) {
                    // currentService was restored 
                    this._fireRegistration(dependencyName, currentService);
                }
            }
        }
        this._testContext = undefined;
    }

    //#endregion Test context


    //#region Error Messages registration

    /**
     * If service is a ServiceWithErrorMessages, this function will register its error messages on the current ErrorMessageService
     * If no ErrorMessageService is registered yet, the messages will be kept and registered on the next registered ErrorMessageService
     * 
     * When a ErrorMessageService is registered, the _unregisteredErrorMessages is cleared (even if in a test context), 
     * if you register a new ErrorMessageService after you need to get messages from the first one if needed 
     * (this function does not do register the initial service messages to the new one)
     */
    protected _registerInErrorMessagesServiceIfNeeded(service: anyReallyAny, dependencyInjectionNames: string | string[]): void {
        const getErrorMessagesFunctionName = service[INJECTOR_GET_ERROR_MESSAGES_FUNCTION_NAME_VAR];
        if (getErrorMessagesFunctionName) {
            if (typeof service[getErrorMessagesFunctionName] === "function") {
                // this is a ServiceWithErrorMessages 
                // We will register its error messages on ErrorMessageService
                const errorMessageService = this.find<ErrorMessageService>(ERROR_MESSAGE_SERVICE);
                if (errorMessageService) {
                    // ErrorMessageService is registered => register error messages
                    errorMessageService.registerErrorMessages(service[getErrorMessagesFunctionName]());
                } else {
                    // ErrorMessageService is not registered yet => keep in _unregisteredErrorMessages
                    this._getCurrentContext().unregisteredErrorMessages.push({ service, getErrorMessagesFunctionName });
                }
            } else {
                throw new Error("service has no " + getErrorMessagesFunctionName + " function, please check @Injectable registerOnErrorMessagesService parameter for " + service);
            }
        }
        if (this._isDependency(ERROR_MESSAGE_SERVICE, dependencyInjectionNames)) {
            // A error message service is being registered
            const errorMessageService = this.get<ErrorMessageService>(ERROR_MESSAGE_SERVICE);// is equivalent to <ErrorMessageService>service
            // register unregisteredErrorMessages from _context
            this._registerErrorMessages(errorMessageService, this._context);
            if (this._testContext) {
                // register unregisteredErrorMessages from _testContext
                this._registerErrorMessages(errorMessageService, this._testContext);
            }
        }
    }

    /**
     * Registers all messages of unregisteredErrorMessages on the current ErrorMessageService (that may not be registered on the passed context !)
     */
    protected _registerErrorMessages(errorMessageService: ErrorMessageService, context: InjectorContext): void {
        for (const urm of context.unregisteredErrorMessages) {
            // register error messages in the order services were registered
            errorMessageService.registerErrorMessages(urm.service[urm.getErrorMessagesFunctionName]());
        }
        context.unregisteredErrorMessages = [];// clear
    }

    //#endregion Error Messages registration

}

export const injector = new Injector();
